import React, { useState } from 'react';
import { api } from '../api';

const FREE_MUSIC = [
  { name: 'NoCopyrightSounds (NCS)', url: 'https://ncs.io', desc: 'Free electronic music for creators' },
  { name: 'Epidemic Sound', url: 'https://www.epidemicsound.com', desc: 'Royalty-free music library (paid)' },
  { name: 'StreamBeats by Harris Heller', url: 'https://www.streambeats.com', desc: 'Free music made for streamers' },
  { name: 'Pretzel Rocks', url: 'https://www.pretzel.rocks', desc: 'Stream-safe music player' },
  { name: 'Pixabay Music', url: 'https://pixabay.com/music', desc: 'Free royalty-free music' },
  { name: 'Uppbeat', url: 'https://uppbeat.io', desc: 'Free music for creators (with credit)' },
];

export default function CopyrightPage() {
  const [form, setForm] = useState({ claimant_name: '', claimant_email: '', content_url: '', original_work: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.claimant_name || !form.claimant_email || !form.description) {
      setStatus('Error: Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    setStatus('');
    try {
      await api.dmcaClaim(form);
      setStatus('Your DMCA claim has been submitted. We will review it within 48 hours.');
      setForm({ claimant_name: '', claimant_email: '', content_url: '', original_work: '', description: '' });
    } catch (e) {
      setStatus(`Error: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dashboard">
      <h1>Copyright & DMCA</h1>

      {/* Policy */}
      <div className="stream-setup">
        <h2>Copyright Policy</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '0.88rem', color: '#adadb8', lineHeight: 1.6 }}>
          <p>Ninty respects intellectual property rights and expects all users to do the same. Streaming copyrighted music, videos, or other content without permission is a violation of our terms.</p>

          <div style={{ padding: '14px', background: '#0e0e10', borderRadius: '4px', border: '1px solid #2a2a2e' }}>
            <div style={{ fontWeight: 800, color: '#efeff1', marginBottom: '6px' }}>What's NOT allowed:</div>
            <ul style={{ paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <li>Playing copyrighted music (Spotify, Apple Music, YouTube, etc.) on stream</li>
              <li>Rebroadcasting TV shows, movies, or other streams</li>
              <li>Using copyrighted images or videos without permission</li>
              <li>Playing full songs even if you "own" them — owning ≠ broadcast rights</li>
            </ul>
          </div>

          <div style={{ padding: '14px', background: '#0e0e10', borderRadius: '4px', border: '1px solid #2a2a2e' }}>
            <div style={{ fontWeight: 800, color: '#efeff1', marginBottom: '6px' }}>What IS allowed:</div>
            <ul style={{ paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <li>Royalty-free / stream-safe music (see resources below)</li>
              <li>Music you created yourself</li>
              <li>Music with explicit permission from the rights holder</li>
              <li>In-game audio and sound effects</li>
              <li>Short, incidental background music (fair use — but be careful)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Strike system */}
      <div className="stream-setup">
        <h2>Copyright Strike System</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {[
            { strike: '1st Strike', action: 'Warning — content removed, VOD muted or deleted', color: '#ffa726' },
            { strike: '2nd Strike', action: '7-day suspension — no streaming, chatting, or tipping', color: '#ff5722' },
            { strike: '3rd Strike', action: 'Permanent ban — account terminated', color: '#eb0400' },
          ].map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: '14px', padding: '14px', background: '#0e0e10', borderRadius: '4px', border: '1px solid #2a2a2e' }}>
              <div style={{ fontFamily: 'var(--font-pixel)', fontSize: '0.6rem', color: s.color, minWidth: '80px', flexShrink: 0, paddingTop: '2px' }}>{s.strike}</div>
              <div style={{ fontSize: '0.85rem', color: '#adadb8' }}>{s.action}</div>
            </div>
          ))}
          <p style={{ fontSize: '0.82rem', color: '#adadb8' }}>
            Strikes expire after 90 days. You can appeal a strike by contacting us at <a href="mailto:copyright@ninty.live" style={{ color: 'var(--accent)' }}>copyright@ninty.live</a>.
          </p>
        </div>
      </div>

      {/* Free music resources */}
      <div className="stream-setup">
        <h2>Copyright-Free Music</h2>
        <p style={{ fontSize: '0.85rem', color: '#adadb8', marginBottom: '14px' }}>Use these resources to find stream-safe music:</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
          {FREE_MUSIC.map(m => (
            <a key={m.name} href={m.url} target="_blank" rel="noopener noreferrer"
              style={{ padding: '14px', background: '#0e0e10', borderRadius: '4px', border: '1px solid #2a2a2e', textDecoration: 'none', transition: 'all 0.1s', boxShadow: '2px 2px 0 rgba(0,0,0,0.3)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2e'; e.currentTarget.style.transform = 'none'; }}
            >
              <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#efeff1', marginBottom: '4px' }}>{m.name}</div>
              <div style={{ fontSize: '0.78rem', color: '#adadb8' }}>{m.desc}</div>
            </a>
          ))}
        </div>
      </div>

      {/* DMCA takedown form */}
      <div className="stream-setup">
        <h2>File a DMCA Claim</h2>
        <p style={{ fontSize: '0.85rem', color: '#adadb8', marginBottom: '16px' }}>
          If you are a copyright holder and believe your work is being used without authorization on Ninty, submit a claim below. We will review and respond within 48 hours.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Your Name (copyright holder) *</label>
            <input className="input" value={form.claimant_name} onChange={e => setForm({ ...form, claimant_name: e.target.value })} required style={{ marginBottom: 0 }} />
          </div>
          <div className="form-group">
            <label>Your Email *</label>
            <input className="input" type="email" value={form.claimant_email} onChange={e => setForm({ ...form, claimant_email: e.target.value })} required style={{ marginBottom: 0 }} />
          </div>
          <div className="form-group">
            <label>Link to infringing content on Ninty</label>
            <input className="input" placeholder="https://ninty.live/..." value={form.content_url} onChange={e => setForm({ ...form, content_url: e.target.value })} style={{ marginBottom: 0 }} />
          </div>
          <div className="form-group">
            <label>Link to your original work</label>
            <input className="input" placeholder="URL to your original content" value={form.original_work} onChange={e => setForm({ ...form, original_work: e.target.value })} style={{ marginBottom: 0 }} />
          </div>
          <div className="form-group">
            <label>Description *</label>
            <textarea className="input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              rows={4} required placeholder="Describe the copyrighted work and how it is being used without authorization..."
              style={{ resize: 'vertical', fontFamily: 'inherit', marginBottom: 0 }} />
          </div>
          <p style={{ fontSize: '0.75rem', color: '#adadb8', marginBottom: '12px' }}>
            By submitting this form, you confirm under penalty of perjury that you are the copyright owner or authorized to act on behalf of the owner, and that the information provided is accurate.
          </p>
          {status && <p style={{ fontSize: '0.85rem', marginBottom: '12px', color: status.startsWith('Error') ? '#eb0400' : '#00c400' }}>{status}</p>}
          <button className="btn btn-primary" type="submit" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit DMCA Claim'}
          </button>
        </form>
      </div>
    </div>
  );
}
